import LegalLayout from './LegalLayout';

export default function TermsOfServicePage() {
  return (
    <LegalLayout title="Terms of Service" effectiveDate="May 2, 2026" lastUpdated="May 2, 2026" policyKey="terms">
      <p>
        These Terms of Service (&ldquo;<strong>Terms</strong>&rdquo;) form a binding agreement
        between you (&ldquo;<strong>you</strong>,&rdquo; &ldquo;<strong>Customer</strong>&rdquo;)
        and Plexura AI Scribe (&ldquo;<strong>Plexura</strong>,&rdquo; &ldquo;<strong>we</strong>,&rdquo;
        &ldquo;<strong>us</strong>&rdquo;) governing your access to and use of the
        Plexura AI Scribe platform, browser extension, marketing site,
        APIs, and related services (collectively, the &ldquo;<strong>Service</strong>&rdquo;).
        By creating an account, clicking &ldquo;I agree,&rdquo; or otherwise
        using the Service, you accept and agree to be bound by these
        Terms. If you do not agree, you may not use the Service.
      </p>
      <p>
        These Terms incorporate by reference our{' '}
        <a href="/legal/privacy">Privacy Policy</a> and{' '}
        <a href="/legal/hipaa">HIPAA Compliance Statement</a>. If you use
        the Service to handle Protected Health Information, our{' '}
        <strong>Business Associate Agreement</strong> (&ldquo;<strong>BAA</strong>&rdquo;) is also
        incorporated and controls in the event of a conflict with these
        Terms regarding PHI.
      </p>

      <h2>1. The Service</h2>
      <p>
        Plexura AI Scribe provides AI-assisted clinical documentation
        tools for licensed mental and behavioral health professionals.
        Features include audio session recording and transcription,
        AI-generated clinical notes (SOAP, psychotherapy progress notes,
        psychiatric evaluations), diagnostic and billing-code
        suggestions, EHR-integration via a browser extension, FHIR/CCD
        exports, practice management, and analytics. The Service is
        provided as a software-as-a-service (SaaS) on a subscription
        basis.
      </p>
      <p>
        <strong>Plexura is a documentation tool, not a medical device.</strong>{' '}
        Output from the Service &mdash; including transcripts, suggested
        diagnoses, suggested billing codes, and clinical notes &mdash;
        constitutes draft work product. The licensed clinician is solely
        responsible for reviewing, correcting, and approving any output
        before signing, billing, or pushing it to an EHR. Plexura makes
        no representation that the Service&rsquo;s output is suitable for
        any particular clinical decision.
      </p>

      <h2>2. Eligibility</h2>
      <p>
        You may use the Service only if all of the following are true:
        you are at least eighteen (18) years old; you are a licensed
        healthcare professional or are using the Service under the direct
        supervision of one; you have legal authority to bind your
        organization to these Terms; and you are not barred from
        receiving the Service under U.S. or other applicable export,
        sanctions, or denied-persons laws.
      </p>

      <h2>3. Accounts and Authentication</h2>
      <p>
        You must provide accurate, current, and complete account
        information and keep it updated. You are responsible for
        maintaining the confidentiality of your credentials and for all
        activities that occur under your account, including activities
        by your invited practice members. Notify us immediately at{' '}
        <a href="mailto:info@plexura.ai">info@plexura.ai</a> if
        you suspect any unauthorized access.
      </p>
      <p>
        Plexura enforces the following authentication safeguards:
      </p>
      <ul>
        <li>Passwords must be at least twelve (12) characters and meet our active complexity policy.</li>
        <li>Failed-login attempts are rate-limited and may trigger temporary lockout.</li>
        <li>Session tokens expire automatically after 120 minutes of issuance.</li>
        <li>Whitelisted-administrator access is restricted to specific email allowlist.</li>
      </ul>

      <h2>4. Subscription, Billing, and Trial</h2>
      <h3>4.1 Plans</h3>
      <p>
        The &ldquo;Professional&rdquo; plan is offered at <strong>$79.99 USD per
        month</strong>, billed monthly via Stripe-hosted recurring
        subscription. The plan includes a <strong>seven (7) day free
        trial</strong> beginning at first sign-up. No payment method is
        required to start the trial; you will be prompted to add one
        before the trial expires. The &ldquo;Enterprise&rdquo; plan is
        offered at custom pricing for multi-clinician practices and is
        governed by a separate Master Services Agreement (MSA).
      </p>
      <h3>4.2 Recurring Billing and Authorization</h3>
      <p>
        By providing payment information, you authorize Plexura, through
        Stripe, to charge the payment method you provided on a recurring
        monthly basis until you cancel. Subscription fees are non-refundable
        except where required by law or as expressly provided in
        Section&nbsp;4.4. All fees are exclusive of applicable taxes,
        which you are responsible for paying.
      </p>
      <h3>4.3 Failed Payments and Dunning</h3>
      <p>
        If a renewal payment fails, Stripe will retry the charge per its
        standard schedule and we will email you to update your payment
        method. If payment is not collected within fourteen (14) days, we
        may suspend or terminate the Service. Reactivation requires a
        successful payment and may not restore access to records that
        were locked during suspension.
      </p>
      <h3>4.4 Cancellation and Refunds</h3>
      <p>
        You may cancel your subscription at any time through the Stripe
        Billing Portal accessible from your account settings. Cancellation
        takes effect at the end of the then-current billing period; you
        retain access through the paid period. Refunds are not provided
        for partial periods, except (a) where required by law, or (b) at
        Plexura&rsquo;s sole discretion when a Service-side fault
        prevented use of the Service for at least seven consecutive days.
      </p>
      <h3>4.5 Price Changes</h3>
      <p>
        Plexura may change subscription pricing on at least thirty (30)
        days&rsquo; notice by email. Changes apply to renewals beginning
        on or after the effective date.
      </p>

      <h2>5. Customer Data and Ownership</h2>
      <p>
        As between you and Plexura, you retain all right, title, and
        interest in and to all data, audio, transcripts, notes, codes,
        and other materials you upload, create, or generate within the
        Service (&ldquo;<strong>Customer Data</strong>&rdquo;). You hereby grant
        Plexura a worldwide, non-exclusive, royalty-free, limited license
        to host, copy, transmit, store, modify, transcode, and display
        Customer Data solely as necessary to provide and improve the
        Service for you, to enforce these Terms, and to comply with law.
        Customer Data that constitutes PHI is also subject to the BAA.
      </p>
      <p>
        We do not use your PHI to train AI models. We may use aggregate,
        de-identified, or anonymized data (created in accordance with
        45 C.F.R. &sect; 164.514(b)) for service-improvement, benchmarking,
        and product analytics.
      </p>

      <h2>6. Acceptable Use</h2>
      <p>You agree not to:</p>
      <ul>
        <li>
          Use the Service to provide care or generate documentation for
          patients you are not licensed to treat in their applicable
          jurisdiction;
        </li>
        <li>
          Record any session without first obtaining the patient&rsquo;s
          informed consent in compliance with state &ldquo;all-party&rdquo;
          or &ldquo;one-party&rdquo; recording-consent laws;
        </li>
        <li>
          Upload PHI for any patient who has not been informed that their
          session may be recorded and transcribed;
        </li>
        <li>
          Use the Service for non-clinical content (e.g., personal
          recordings, journalism, educational lectures) unless contracted
          for that purpose;
        </li>
        <li>
          Attempt to reverse-engineer, decompile, disassemble, or
          otherwise derive the source code of the Service, except where
          this restriction is prohibited by applicable law;
        </li>
        <li>
          Probe, scan, or test the vulnerability of the Service without
          our prior written consent (responsible disclosures may be sent
          to <a href="mailto:info@plexura.ai">info@plexura.ai</a>);
        </li>
        <li>
          Bypass or disable security or access-control features,
          including rate limits, audit logging, and session timeouts;
        </li>
        <li>
          Resell, sublicense, or grant access to the Service to third
          parties outside your authorized practice members;
        </li>
        <li>
          Use the Service to violate any applicable law, including
          HIPAA, state medical-licensing rules, anti-spam laws, or
          intellectual property rights;
        </li>
        <li>
          Upload malware, viruses, or any data designed to interfere
          with or harm the Service or other users.
        </li>
      </ul>

      <h2>7. Patient Consent and Recording Compliance</h2>
      <p>
        You are solely responsible for obtaining and documenting valid,
        informed, and lawful consent from every patient before recording,
        transcribing, or processing their session through the Service.
        Recording-consent laws vary by state and country; some
        jurisdictions require all parties to consent. The Service
        provides a built-in consent-tracking flag, but the legal
        sufficiency of consent is your responsibility as the treating
        clinician. Plexura disclaims any liability arising from your
        failure to obtain or document patient consent.
      </p>

      <h2>8. EHR Integration and the Browser Extension</h2>
      <p>
        Our browser extension reads the active EHR tab&rsquo;s DOM only
        when you explicitly invoke a push action and only to write
        Plexura-generated note content into the EHR field you have
        targeted. The extension does not read existing chart contents or
        transmit DOM data back to Plexura beyond the metadata required to
        confirm a successful push. <strong>You are responsible for
        verifying that the content pushed to the EHR is correct,
        complete, and properly attributed before signing the note within
        the EHR.</strong> Plexura is not responsible for downstream EHR
        accuracy, regulatory compliance, or billing-code adjudication
        once the note has been pushed to your EHR.
      </p>

      <h2>9. AI Output and Clinical Responsibility</h2>
      <p>
        Outputs generated by the Service&rsquo;s AI components &mdash;
        including diagnostic suggestions, ICD-10 / CPT / E&amp;M code
        suggestions, narrative summaries, and template-formatted notes
        &mdash; are <strong>clinical drafts</strong> intended to assist,
        not replace, your professional judgment. AI models can produce
        errors, omissions, hallucinations, and outdated information. You
        agree:
      </p>
      <ul>
        <li>
          To independently review every Service output before signing,
          billing, or pushing it to a patient record;
        </li>
        <li>
          That you remain the licensed clinician of record and bear
          full responsibility for the clinical and billing accuracy of
          the final note;
        </li>
        <li>
          That nothing in the Service is intended to constitute medical
          advice, diagnosis, or treatment by Plexura;
        </li>
        <li>
          That AI suggestions do not create a clinician-patient
          relationship between Plexura and any patient.
        </li>
      </ul>

      <h2>10. Third-Party Services</h2>
      <p>
        The Service integrates with third-party platforms (including but
        not limited to Stripe, Tebra, Athena, Epic, Cerner, AdvancedMD,
        DrChrono, Practice Fusion, OpenAI, MongoDB Atlas, Resend, and
        Emergent infrastructure). Your use of those platforms is governed
        by their own terms and privacy policies. Plexura is not
        responsible for the availability, security, or content of
        third-party services. Where a third party is also a Business
        Associate, the BAA in place between Plexura and that third party
        protects PHI flowing through the integration.
      </p>

      <h2>11. Intellectual Property</h2>
      <p>
        The Service, including its software, design, text, graphics,
        logos, and trademarks, is owned by Plexura or its licensors and
        is protected by U.S. and international intellectual-property
        laws. We grant you a limited, revocable, non-exclusive,
        non-transferable license to use the Service solely for your
        internal business purposes during your active subscription. All
        rights not expressly granted are reserved.
      </p>
      <p>
        If you submit ideas, suggestions, or feedback (&ldquo;<strong>Feedback</strong>&rdquo;),
        you grant Plexura a perpetual, irrevocable, royalty-free license
        to use the Feedback for any purpose without restriction or
        compensation.
      </p>

      <h2>12. Confidentiality</h2>
      <p>
        Each party agrees to keep confidential any non-public information
        disclosed by the other party that is marked confidential or that
        a reasonable person would understand to be confidential. This
        obligation survives termination for three (3) years, except for
        trade secrets, which remain confidential for so long as they
        qualify as such under applicable law. PHI is governed exclusively
        by the BAA and HIPAA.
      </p>

      <h2>13. Suspension and Termination</h2>
      <p>
        Plexura may suspend or terminate your access to the Service
        immediately, with or without notice, if (a) you breach these
        Terms or the BAA, (b) you fail to pay fees when due, (c) we
        reasonably believe your activity poses a security risk or harms
        the Service or other users, or (d) we are required to do so by
        law. You may terminate at any time by canceling your subscription
        as described in Section&nbsp;4.4.
      </p>
      <p>
        Upon termination, your access ceases. We will retain Customer
        Data for up to thirty (30) days during which you may export it
        through the Service or by emailing{' '}
        <a href="mailto:info@plexura.ai">info@plexura.ai</a>. After
        thirty (30) days, we will delete or anonymize Customer Data
        except for records we are legally required to retain (e.g.,
        billing, audit logs, breach-investigation records). PHI return
        and destruction follow the BAA.
      </p>

      <h2>14. Disclaimers</h2>
      <p>
        EXCEPT AS EXPRESSLY PROVIDED IN THESE TERMS, THE SERVICE IS
        PROVIDED &ldquo;AS IS&rdquo; AND &ldquo;AS AVAILABLE&rdquo;
        WITHOUT WARRANTIES OF ANY KIND, EXPRESS OR IMPLIED, INCLUDING
        IMPLIED WARRANTIES OF MERCHANTABILITY, FITNESS FOR A PARTICULAR
        PURPOSE, NON-INFRINGEMENT, AND ANY WARRANTIES ARISING FROM COURSE
        OF DEALING OR USAGE OF TRADE. PLEXURA DOES NOT WARRANT THAT THE
        SERVICE WILL BE UNINTERRUPTED, ERROR-FREE, SECURE FROM ALL
        ATTACK, OR THAT AI OUTPUTS WILL BE ACCURATE OR FREE FROM
        HALLUCINATION OR BIAS. PLEXURA DISCLAIMS ANY LIABILITY FOR
        CLINICAL DECISIONS MADE IN RELIANCE ON SERVICE OUTPUT.
      </p>

      <h2>15. Limitation of Liability</h2>
      <p>
        TO THE MAXIMUM EXTENT PERMITTED BY LAW, PLEXURA AND ITS
        OFFICERS, DIRECTORS, EMPLOYEES, AGENTS, AFFILIATES, AND
        LICENSORS WILL NOT BE LIABLE FOR ANY INDIRECT, INCIDENTAL,
        SPECIAL, CONSEQUENTIAL, EXEMPLARY, OR PUNITIVE DAMAGES, OR FOR
        LOSS OF PROFITS, REVENUE, GOODWILL, USE, OR DATA, ARISING OUT OF
        OR RELATED TO YOUR USE OF THE SERVICE, EVEN IF ADVISED OF THE
        POSSIBILITY OF SUCH DAMAGES. PLEXURA&rsquo;S TOTAL AGGREGATE
        LIABILITY FOR ANY CLAIM ARISING OUT OF OR RELATED TO THESE TERMS
        OR THE SERVICE WILL NOT EXCEED THE TOTAL FEES PAID BY YOU TO
        PLEXURA IN THE TWELVE (12) MONTHS IMMEDIATELY PRECEDING THE
        EVENT GIVING RISE TO THE CLAIM. THESE LIMITATIONS DO NOT APPLY
        TO LIABILITY THAT CANNOT BE LIMITED UNDER APPLICABLE LAW
        (INCLUDING GROSS NEGLIGENCE, WILLFUL MISCONDUCT, AND DEATH OR
        BODILY INJURY CAUSED BY PLEXURA&rsquo;S NEGLIGENCE).
      </p>

      <h2>16. Indemnification</h2>
      <p>
        You will defend, indemnify, and hold harmless Plexura from and
        against any third-party claim, demand, or proceeding arising
        out of or related to: (a) your breach of these Terms or the
        BAA; (b) your violation of any law or regulation applicable to
        your professional practice; (c) your failure to obtain valid
        patient consent; (d) clinical decisions you made based on
        Service output; or (e) Customer Data you uploaded that
        infringes a third party&rsquo;s rights. We will promptly notify
        you of the claim, allow you to control its defense (with
        counsel reasonably acceptable to us), and reasonably cooperate
        at your expense.
      </p>

      <h2>17. Governing Law and Dispute Resolution</h2>
      <p>
        These Terms are governed by the laws of the State of Delaware,
        United States, excluding its conflict-of-laws principles. Any
        dispute arising out of or relating to these Terms or the
        Service will be resolved exclusively by binding arbitration
        administered by JAMS in San Francisco, California, before a
        single arbitrator under the JAMS Streamlined Arbitration Rules,
        except that either party may seek injunctive or equitable
        relief in court for intellectual-property or confidentiality
        matters. <strong>YOU AND PLEXURA EACH WAIVE ANY RIGHT TO A JURY
        TRIAL AND TO PARTICIPATE IN A CLASS ACTION.</strong> If
        arbitration is unenforceable for any reason, the parties submit
        to the exclusive jurisdiction of the state and federal courts
        located in Delaware.
      </p>

      <h2>18. Modifications</h2>
      <p>
        Plexura may revise these Terms from time to time. Material
        changes take effect thirty (30) days after we post the revised
        version and notify you by email. Continued use of the Service
        after the effective date constitutes acceptance. If you do not
        agree to a revision, your sole remedy is to cancel your
        subscription before it takes effect.
      </p>

      <h2>19. Notices</h2>
      <p>
        Notices to Plexura must be sent to{' '}
        <a href="mailto:info@plexura.ai">info@plexura.ai</a>. Notices
        to you will be delivered to the email associated with your
        account or posted in the Service.
      </p>

      <h2>20. Miscellaneous</h2>
      <ul>
        <li>
          <strong>Entire agreement.</strong> These Terms, together with
          the Privacy Policy, HIPAA Compliance Statement, BAA, and any
          executed order form, constitute the entire agreement between
          the parties and supersede all prior agreements.
        </li>
        <li>
          <strong>Severability.</strong> If any provision is held
          unenforceable, the remaining provisions remain in full force.
        </li>
        <li>
          <strong>No waiver.</strong> Failure to enforce any right is not
          a waiver of that right.
        </li>
        <li>
          <strong>Assignment.</strong> You may not assign these Terms
          without our prior written consent. We may assign in connection
          with a merger, acquisition, or sale of assets.
        </li>
        <li>
          <strong>Force majeure.</strong> Neither party will be liable for
          delay or failure caused by events beyond its reasonable control.
        </li>
        <li>
          <strong>Survival.</strong> Sections 5, 11&ndash;17, and 20
          survive termination.
        </li>
      </ul>

      <h2>21. Contact</h2>
      <p>
        Plexura AI Scribe<br />
        Email: <a href="mailto:info@plexura.ai">info@plexura.ai</a>
      </p>
      <p>
        Use the same address for legal, support, and security matters &mdash;
        all of which route to our team and are triaged within one business day.
      </p>
    </LegalLayout>
  );
}
